import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { getSharedLang, subscribeSharedLang } from '../../lib/base-i18n';
import { tOss } from '../../lib/oss-i18n';
import './OssLicensesPage.css';

/**
 * OssLicensesPage (oss-notices — 2026-07-10) — 오픈소스 라이선스 고지.
 *
 * 로그인 없이 접근 가능한 공개 라우트다(`/oss-licenses`). 배포본을 받은 사람은 누구나 고지를
 * 볼 수 있어야 하므로 AuthGuard 밖에 둔다.
 *
 * 본문은 `public/oss/third-party-notices.txt` 를 런타임에 fetch 한다. 344KB 짜리 라이선스
 * 원문을 JS 번들에 문자열로 넣으면 앱 전체가 무거워지고, 정적 파일이면 엣지가 캐시한다.
 * 문서는 `scripts/gen-third-party-notices.mjs` 가 생성한다 — 손으로 편집하지 않는다.
 *
 * 본문은 각 패키지의 라이선스 원문이라 번역하지 않는다(원문 변경은 라이선스 위반).
 */
const NOTICES_URL = '/oss/third-party-notices.txt';

export function OssLicensesPage() {
  useSyncExternalStore(subscribeSharedLang, getSharedLang, getSharedLang);
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(NOTICES_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="oss-page">
      <header className="oss-page-header">
        <h1 className="oss-page-title">{tOss('oss.title')}</h1>
        <p className="oss-page-subtitle">{tOss('oss.subtitle')}</p>
        <div className="oss-page-actions">
          <a href={NOTICES_URL} target="_blank" rel="noopener noreferrer" className="oss-page-action">
            {tOss('oss.download')}
          </a>
          <Link to="/" className="oss-page-action">
            {tOss('oss.back')}
          </Link>
        </div>
      </header>

      {error ? (
        <p className="oss-page-status oss-page-status--error" role="status">
          {tOss('oss.error')}
        </p>
      ) : text === null ? (
        <p className="oss-page-status" role="status">
          {tOss('oss.loading')}
        </p>
      ) : (
        // 라이선스 원문은 공백·줄바꿈이 의미를 가진다(Apache 부록 들여쓰기 등) → <pre> 로 그대로.
        <pre className="oss-page-body">{text}</pre>
      )}
    </main>
  );
}
